import { StyleProp } from 'react-native';
import { Trans } from 'react-i18next';
import React, { useMemo } from 'react';

import { Badge } from '@components/Badge';
import { Card } from '@components/Card';
import { DriverInfo } from '@components/DriverInfo';
import { OrderSummary } from '@lib/types';
import { StyleObj } from '@providers/Theme/types';
import { Typography } from '@components/Typography';
import { makeStyles } from '@providers/Theme';
import { useLiveValue } from '@hooks/useLiveValue';
import { useTimeDistance } from '@hooks/useTimeDistance';
import { useTranslation } from '@providers/I18n';

import { isOrderLate } from './utils/isOrderLate';

import { OrderDetailsRow } from '../OrderDetailsRow';

const useStyles = makeStyles<{ isSelected: boolean }>((theme) => ({
  root: {
    ...theme.mixins.padding(2),
  },
  selected: {
    backgroundColor: theme.palette.primary[600],
  },
  late: {
    ...theme.mixins.border(theme.palette.primary[600]!),
  },
  title: (props) => ({
    color: props.isSelected ? 'white' : theme.palette.base[900],
  }),
}));

type OrderSummaryCardProps = {
  onClick?: () => void;
  order: OrderSummary;
  isSelected?: boolean;
  style?: StyleProp<StyleObj>;
};

export const OrderSummaryCard: React.FC<OrderSummaryCardProps> = ({
  onClick,
  isSelected = false,
  style,
  order,
}) => {
  const { t, i18n } = useTranslation();
  const styles = useStyles({ isSelected });

  const isLate = useLiveValue(() => isOrderLate(order), 10 * 1000); // recalculate every 10 seconds

  const createdSince = useTimeDistance(order.createdAt);

  const badge = useMemo(() => {
    if (isLate) {
      return undefined;
    }
    if (['PENDING', 'ROUTED', 'SEEN'].includes(order.state)) {
      return (
        <Badge
          label={t('orderSummaryCard.badge.new', 'New')}
          preset="warning"
        />
      );
    } else if (order.state === 'INSPECTING') {
      return (
        <Badge
          label={t('orderSummaryCard.badge.Inspected', 'Inspected')}
          preset="warning"
        />
      );
    } else if (order.state === 'ACCEPTED' || order.state === 'FOOD_IS_READY') {
      return (
        <Badge
          label={t('orderSummaryCard.badge.accepted', 'Accepted')}
          preset="success"
        />
      );
    } else if (order.state === 'OUT_FOR_DELIVERY') {
      return (
        <Badge
          label={t(
            'orderSummaryCard.badge.out_for_delivery',
            'Out for delivery',
          )}
          preset="success"
        />
      );
    } else if (order.state === 'DELIVERED') {
      return (
        <Badge
          label={t('orderSummaryCard.badge.delivered', 'Delivered')}
          preset="success"
        />
      );
    } else if (order.state === 'CANCELED') {
      return (
        <Badge
          label={t('orderSummaryCard.badge.canceled', 'Canceled')}
          preset="error"
        />
      );
    } else if (order.state === 'REJECTED') {
      return (
        <Badge
          label={t('orderSummaryCard.badge.rejected', 'Rejected')}
          preset="error"
        />
      );
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.state, i18n.language]);

  return (
    <Card
      onPress={onClick}
      style={[
        styles.root,
        isSelected && styles.selected,
        isLate && styles.late,
        style,
      ]}>
      <OrderDetailsRow
        color={(theme) => (isSelected ? 'white' : theme.palette.grey[900])}
        text={t('orderSummaryCard.id', {
          defaultValue: 'ID: {{id}}',
          id: order.shortCode,
        })}
        EndIconProps={
          isLate
            ? { name: 'alarm', color: isSelected ? 'white' : 'red', size: 20 }
            : undefined
        }
        TypographyProps={{
          size: 'xl',
          weight: 'semiBold',
          style: styles.title,
        }}
        endNode={badge}
      />

      <OrderDetailsRow
        StartIconProps={{ name: 'clock' }}
        color={(theme) => {
          if (isSelected) {
            return 'white';
          }

          return isLate ? theme.palette.error[300] : theme.palette.grey[700];
        }}
        text={createdSince}
      />

      <OrderDetailsRow
        color={(theme) => (isSelected ? 'white' : theme.palette.grey[700])}
        StartIconProps={{ name: 'user' }}
        text={order.userSummary.name}
      />

      <OrderDetailsRow
        color={(theme) => (isSelected ? 'white' : theme.palette.grey[700])}
        StartIconProps={{ name: 'phone' }}
        text={order.userSummary.phone}
      />

      {!order.isFleet && (
        <OrderDetailsRow
          color={(theme) => (isSelected ? 'white' : theme.palette.grey[700])}
          StartIconProps={{ name: 'target' }}
          text={order.userSummary.address}
        />
      )}

      {order.tripDriverCache && (
        <DriverInfo
          driver={order.tripDriverCache}
          showTracking={false}
          isSelected={isSelected}
        />
      )}

      {order.assignedLiveTrackerAgent && (
        <OrderDetailsRow
          color={(theme) => (isSelected ? 'white' : theme.palette.grey[700])}
          text={
            <Trans
              i18nKey="orderSummaryCard.assignee"
              defaults="<0>Assigned to:</0> {{assignee}}"
              values={{
                assignee: order.assignedLiveTrackerAgent.userCode,
              }}
              // @ts-ignore
              components={[<Typography size="sm" weight="semiBold" />]}
            />
          }
        />
      )}
    </Card>
  );
};
